import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";

import Section from "../layout/Section";
import Container from "../layout/Container";

import { fadeUp, stagger } from "@/utils/motion";

const platforms = [
  {
    name: "Airbnb",
    src: "https://upload.wikimedia.org/wikipedia/commons/6/69/Airbnb_Logo_Bélo.svg",
    kind: "image",
  },
  {
    name: "Booking",
    src: "https://upload.wikimedia.org/wikipedia/commons/6/6b/Booking.com_Icon_2022.svg",
    kind: "image",
  },
  {
    name: "Vrbo",
    kind: "inline",
  },
];

function PlatformBadge({ platform }) {
  return (
    <div
      className="
        flex items-center gap-2
        px-3 py-1.5
        rounded-pill
        bg-bg-light
        border border-border-soft
        text-text-primary
        font-medium
      "
    >
      {platform.kind === "image" ? (
        <img
          src={platform.src}
          alt={platform.name}
          className="w-4 h-4 object-contain"
        />
      ) : (
        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-text-primary text-bg-surface">
          <span className="text-[9px] font-bold leading-none">V</span>
        </div>
      )}

      <span className="text-small">{platform.name}</span>
    </div>
  );
}

export default function HeroPremium() {
  const navigate = useNavigate();

  const [beds, setBeds] = useState(2);
  const [baths, setBaths] = useState(1);
  const [displayValue, setDisplayValue] = useState(0);
  const [showCTA, setShowCTA] = useState(false);

  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const valueRef = useRef(0);

  const estimate = useMemo(() => {
    const perRoom = 14000; // blended US + Canada realistic avg
    return Math.round(perRoom * beds);
  }, [beds]);

  const monthly = Math.round(estimate / 12);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);

    const start = valueRef.current;
    const end = estimate;
    const duration = 450;
    let startTime = null;

    const animateValue = (t) => {
      if (!startTime) startTime = t;

      const progress = Math.min((t - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(start + (end - start) * eased);

      valueRef.current = next;
      setDisplayValue(next);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animateValue);
      }
    };

    rafRef.current = requestAnimationFrame(animateValue);

    return () => cancelAnimationFrame(rafRef.current);
  }, [estimate]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const triggerCTA = useCallback(() => {
    setShowCTA(true);
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setShowCTA(false);
    }, 2600);
  }, []);

  const handleBedsChange = useCallback(
    (value) => {
      const newBeds = Number(value);
      setBeds(newBeds);

      if (baths > newBeds) {
        setBaths(newBeds);
      }

      triggerCTA();
    },
    [baths, triggerCTA]
  );

  const handleBathChange = useCallback(
    (value) => {
      setBaths(Number(value));
      triggerCTA();
    },
    [triggerCTA]
  );

  const goToCalc = useCallback(() => {
    navigate("/revenue-calc");
  }, [navigate]);

  return (
    <Section
      id="hero"
      className="bg-bg-surface relative overflow-hidden pt-4 pb-8 lg:py-10"
      padding="md"
    >
      <Container>
        <div className="grid gap-10 lg:grid-cols-[1.12fr_0.88fr_1.08fr] lg:items-stretch lg:min-h-screen">
          {/* LEFT */}
          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            animate="show"
            className="relative flex flex-col justify-center text-center lg:text-left"
          >
            <motion.p
              variants={fadeUp}
              className="text-small font-semibold tracking-wide text-brand-primary uppercase"
            >
              Master the art of short term rentals
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="mt-3 text-hero font-extrabold leading-[1.1]"
            >
              We build the system.
              <br />
              You collect the returns.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-4 text-body text-text-muted max-w-content mx-auto lg:mx-0"
            >
              Zenia manages pricing, operations, guest communication, and
              cleaning so your property performs like a premium asset without
              the day-to-day workload.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
            >
              <Link to="/contact" className="btn-primary btn-md">
                Book Consultation
              </Link>

              <Link to="/solutions" className="btn-outline btn-md">
                View Solutions
              </Link>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="mt-7 flex flex-col items-center lg:items-start gap-4"
            >
              <div className="text-micro uppercase tracking-wide text-text-muted">
                We manage your listings across
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-3">
                {platforms.map((platform) => (
                  <PlatformBadge key={platform.name} platform={platform} />
                ))}
              </div>

              <div className="text-small text-text-muted text-center lg:text-left">
                Proudly hosted{" "}
                <span className="font-semibold text-text-primary">
                  1,000+ guests
                </span>{" "}
                across our curated stays
              </div>
            </motion.div>
          </motion.div>

          {/* CALCULATOR */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-stretch justify-center"
          >
            <div
              onMouseEnter={triggerCTA}
              onTouchStart={triggerCTA}
              className="relative w-full max-w-content self-center"
            >
              <div className="rounded-xl p-5 shadow-card bg-brand-primary text-white">
                <div className="flex justify-between mb-4">
                  <span className="text-body font-semibold text-accent-gold">
                    Airbnb Earnings
                  </span>
                  <span className="text-small text-white/60">
                    Quick estimate
                  </span>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-body mb-1">
                    <span>Bedrooms</span>
                    <span>{beds}</span>
                  </div>

                  <input
                    type="range"
                    min={1}
                    max={4}
                    value={beds}
                    onChange={(e) => handleBedsChange(e.target.value)}
                    className="w-full accent-white"
                  />
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-body mb-1">
                    <span>Bathrooms</span>
                    <span>{baths}</span>
                  </div>

                  <input
                    type="range"
                    min={1}
                    max={beds}
                    value={baths}
                    onChange={(e) => handleBathChange(e.target.value)}
                    className="w-full accent-white"
                  />
                </div>

                <div className="mt-4">
                  <div className="text-small text-white/70">
                    Estimated yearly earnings
                  </div>

                  <div className="text-title font-semibold mt-1">
                    ${displayValue.toLocaleString()}
                  </div>

                  <div className="text-small text-white/80 mt-1">
                    ≈ ${monthly.toLocaleString()} / month
                  </div>
                </div>
              </div>

              {showCTA && (
                <Link
                  to="/revenue-calc"
                  className="
                    absolute left-1/2 -translate-x-1/2 bottom-[-20px]
                    bg-bg-surface px-4 py-2 rounded-pill text-small font-semibold shadow-card cursor-pointer
                  "
                  onClick={goToCalc}
                >
                  Get full report →
                </Link>
              )}
            </div>
          </motion.div>

          {/* PREMIUM VISUAL */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="relative flex h-full min-h-[36rem] justify-center"
          >
            <div className="grid h-full w-full grid-rows-2 gap-5">
              <motion.div
                animate={{ y: [0, -8, 0], rotate: [-1.5, 0.75, -1.5] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="
                  group relative overflow-hidden
                  rounded-2xl shadow-card border border-border-soft
                  bg-bg-surface
                "
              >
                <img
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80"
                  alt="Luxury apartment interior"
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-black/10" />

                <div className="absolute left-0 right-0 top-0 p-5">
                  <div className="inline-flex rounded-pill bg-white/12 px-3 py-1 text-small font-semibold text-white backdrop-blur">
                    Premium stay ready
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="text-[1.45rem] font-semibold leading-tight tracking-tight text-white">
                    High-demand city inventory
                  </h3>
                  <p className="mt-2 text-small text-white/85 max-w-[22rem]">
                    Styled for premium guest experience, stronger occupancy, and
                    better perceived value.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-pill bg-white/12 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      Design-led
                    </span>
                    <span className="rounded-pill bg-white/12 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      Revenue-ready
                    </span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0], rotate: [1.25, -0.5, 1.25] }}
                transition={{
                  duration: 7.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.35,
                }}
                className="
                  group relative overflow-hidden
                  rounded-2xl shadow-card border border-border-soft
                  bg-bg-surface
                "
              >
                <img
                  src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80"
                  alt="Premium villa stay"
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                <div className="absolute inset-0 bg-black/10" />

                <div className="absolute left-0 right-0 top-0 p-5">
                  <div className="inline-flex rounded-pill bg-white/12 px-3 py-1 text-small font-semibold text-white backdrop-blur">
                    Elite hospitality
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="text-[1.45rem] font-semibold leading-tight tracking-tight text-white">
                    Full-service management
                  </h3>
                  <p className="mt-2 text-small text-white/85 max-w-[22rem]">
                    Operations, guest support, pricing, and turnover handled in
                    one premium system.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-pill bg-white/12 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      Seamless ops
                    </span>
                    <span className="rounded-pill bg-white/12 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      Higher yield
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
}